import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Divider,
  FormLayout,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";

import {
  disconnectLine,
  getMe,
  removeLineRecipient,
  rotateLineLinkCode,
  updateNotificationChannels,
  updateNotifications,
  type NotificationChannelsPayload,
} from "../api/me";
import { UpgradeNote } from "../components/PlanGate";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { usePlan } from "../hooks/usePlan";
import { formatDateTime } from "../utils/date";

import { t } from "../lib/i18n";

/** コピー完了表示を戻すまでの時間。 */
const COPIED_FLASH_MS = 2000;
/** 「保存しました」を消すまでの時間。 */
const SAVED_FLASH_MS = 3000;

export function NotificationSettings() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { allows } = usePlan();
  const canUseEmail = allows("reorder");
  const canUseSlack = allows("slack");

  const [email, setEmail] = useState("");
  const [slackUrl, setSlackUrl] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const handleCopyCode = (code: string) => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), COPIED_FLASH_MS);
    });
  };

  useEffect(() => {
    if (meQuery.data) {
      setEmail(meQuery.data.alert_email ?? "");
      setSlackUrl(meQuery.data.alert_slack_webhook_url ?? "");
    }
  }, [meQuery.data]);

  const invalidateMe = () =>
    queryClient.invalidateQueries({ queryKey: ["me"] });

  const mutation = useMutation({
    mutationFn: () =>
      updateNotifications({
        // メール通知は発注点アラートと不可分なので Starter 以上。降格後も
        // 保存済みアドレスは再アップグレードに備えて保持する。
        ...(canUseEmail ? { alert_email: email.trim() || null } : {}),
        // 降格後も保存済みURLは再アップグレードに備えて保持する。ただし
        // プラン外の disabled フィールドを送ると、Slack の403に巻き込まれて
        // メールアドレスまで保存できないため、利用可能なときだけ送信する。
        ...(canUseSlack
          ? { alert_slack_webhook_url: slackUrl.trim() || null }
          : {}),
      }),
    onSuccess: () => {
      invalidateMe();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), SAVED_FLASH_MS);
    },
  });

  // チャネルのオン/オフは押した瞬間に反映する（保存し忘れで通知が止まらない）。
  const channelMutation = useMutation({
    mutationFn: (body: NotificationChannelsPayload) =>
      updateNotificationChannels(body),
    onSuccess: invalidateMe,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectLine,
    onSuccess: invalidateMe,
  });

  const removeRecipientMutation = useMutation({
    mutationFn: removeLineRecipient,
    onSuccess: invalidateMe,
  });

  const rotateCodeMutation = useMutation({
    mutationFn: rotateLineLinkCode,
    onSuccess: invalidateMe,
  });

  const me = meQuery.data;
  const line = me?.line;
  const recipients = line?.recipients ?? [];
  const remainingSlots = (line?.max_recipients ?? 0) - recipients.length;

  return (
    <Page
      title={t("通知設定")}
      primaryAction={{
        content: t("保存"),
        loading: mutation.isPending,
        // データ未着（ロード中・取得失敗）で空フォームのまま保存させない
        //（取得失敗中に保存できると、既存の設定を空値で上書きし得る）。
        disabled: !me,
        onAction: () => mutation.mutate(),
      }}
    >
      <Layout>
        <Layout.Section>
          {meQuery.isLoading ? (
            <Card>
              <SkeletonBodyText lines={4} />
            </Card>
          ) : meQuery.isError ? (
            <QueryErrorBanner
              title={t("通知設定の取得に失敗しました")}
              onRetry={() => void meQuery.refetch()}
            />
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t("在庫低下・在庫切れアラートを送信する宛先を設定します。メール・Slack・LINE は併用でき、オンにしたチャネルすべてに送信されます。")}
                </Text>
                <FormLayout>
                  <BlockStack gap="200">
                    <Checkbox
                      label={t("メールで通知する")}
                      checked={me?.alert_email_enabled ?? true}
                      disabled={channelMutation.isPending || !me || !canUseEmail}
                      onChange={(value) =>
                        channelMutation.mutate({ alert_email_enabled: value })
                      }
                      helpText={t("オフにしてもアドレスは残ります（再開はここをオンに戻すだけ）。")}
                    />
                    <TextField
                      label={t("通知メールアドレス")}
                      type="email"
                      value={email}
                      onChange={setEmail}
                      placeholder="alerts@example.com"
                      autoComplete="email"
                      disabled={!canUseEmail}
                    />
                  </BlockStack>
                  {!canUseEmail && (
                    <UpgradeNote feature="reorder">
                      <p>
                        {t("メール通知はStarterプラン以上で利用できます。発注点を割った商品を欠品前にお知らせします。")}
                      </p>
                    </UpgradeNote>
                  )}
                  <BlockStack gap="200">
                    <Checkbox
                      label={t("Slack で通知する")}
                      checked={me?.alert_slack_enabled ?? true}
                      disabled={channelMutation.isPending || !me || !canUseSlack}
                      onChange={(value) =>
                        channelMutation.mutate({ alert_slack_enabled: value })
                      }
                    />
                    <TextField
                      label="Slack Incoming Webhook URL"
                      value={slackUrl}
                      onChange={setSlackUrl}
                      placeholder="https://hooks.slack.com/services/..."
                      autoComplete="off"
                      disabled={!canUseSlack}
                      helpText={t("Slack ワークスペースで Incoming Webhook を作成し、URL を貼り付けてください")}
                    />
                  </BlockStack>
                  {!canUseSlack && <UpgradeNote feature="slack" />}
                </FormLayout>
                {channelMutation.isError && (
                  <Banner tone="critical">
                    {t("通知のオン/オフを保存できませんでした:")}{" "}
                    {channelMutation.error.message}
                  </Banner>
                )}
                {savedFlash && <Banner tone="success">{t("設定を保存しました")}</Banner>}
                {mutation.isError && (
                  <Banner tone="critical">
                    {t("保存に失敗しました:")} {mutation.error.message}
                  </Banner>
                )}
                <InlineStack align="end">
                  <Button
                    variant="primary"
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={!me}
                  >
                    {t("保存")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        <Layout.Section>
          {line && (
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    {t("LINE通知")}
                  </Text>
                  {/* 🔴 「受信中」と言えるのは配信条件（プラン内かつトグルON）が
                      揃っているときだけ。連携の有無（linked）だけで緑バッジを出すと、
                      オフにした店・Proから落ちた店に「届いている」と嘘をつく＝
                      ブロックされた人を受信中と言い続けた旧バグと同じ形。 */}
                  {line.linked &&
                    (line.available && (me?.alert_line_enabled ?? true) ? (
                      <Badge tone="success">
                        {t("{count}人が受信中", {
                          count: String(recipients.length),
                        })}
                      </Badge>
                    ) : (
                      <Badge>
                        {t("{count}人が登録済み（通知はオフ）", {
                          count: String(recipients.length),
                        })}
                      </Badge>
                    ))}
                </InlineStack>

                {!line.available ? (
                  // 🔴 すぐ上の Slack ゲートと同じ UpgradeNote に揃える＝
                  // 「アップグレードすると」と言いながら行き先が無いバナーにしない。
                  <UpgradeNote feature="line">
                    <p>
                      {t("LINE通知はProプラン以上の特典です。プランをアップグレードすると、在庫アラートを LINE の1日ダイジェストで受け取れます。")}
                    </p>
                  </UpgradeNote>
                ) : (
                  <BlockStack gap="400">
                    <Checkbox
                      label={t("LINE で通知する")}
                      checked={me?.alert_line_enabled ?? true}
                      disabled={channelMutation.isPending || !me}
                      onChange={(value) =>
                        channelMutation.mutate({ alert_line_enabled: value })
                      }
                      helpText={t("オフにしても連携は残ります（再開はここをオンに戻すだけ）。")}
                    />

                    {recipients.length > 0 && (
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {t("受信中のメンバー")}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t("心当たりのない相手が混ざっていたら解除してください。連携コードを知っている人は誰でもここに並びます。")}
                        </Text>
                        {recipients.map((recipient) => (
                          <BlockStack gap="200" key={recipient.id}>
                            <Divider />
                            <InlineStack
                              align="space-between"
                              blockAlign="center"
                              gap="200"
                            >
                              <BlockStack gap="050">
                                <Text as="span" variant="bodyMd">
                                  {recipient.display_name ?? t("（名前を取得できませんでした）")}
                                </Text>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {t("連携日時")}: {formatDateTime(recipient.linked_at)}
                                </Text>
                              </BlockStack>
                              <Button
                                tone="critical"
                                variant="tertiary"
                                loading={
                                  removeRecipientMutation.isPending &&
                                  removeRecipientMutation.variables ===
                                    recipient.id
                                }
                                onClick={() =>
                                  removeRecipientMutation.mutate(recipient.id)
                                }
                              >
                                {t("解除")}
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        ))}
                        {removeRecipientMutation.isError && (
                          <Banner tone="critical">
                            {t("解除に失敗しました:")}{" "}
                            {removeRecipientMutation.error.message}
                          </Banner>
                        )}
                      </BlockStack>
                    )}

                    <Divider />

                    <BlockStack gap="300">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {t("メンバーを追加する")}
                      </Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {t("店舗のスタッフを最大{max}人まで追加できます。追加したい人それぞれが、次の2ステップを行ってください（あと{count}人）。", {
                          max: String(line.max_recipients),
                          count: String(Math.max(remainingSlots, 0)),
                        })}
                      </Text>
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {t("ステップ1: 公式アカウント（Bot）を友だち追加")}
                        </Text>
                        {line.add_friend_url && (
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" tone="subdued">
                              {t("スマホの方: ボタンをタップすると LINE が開きます")}
                            </Text>
                            <InlineStack>
                              <Button
                                url={line.add_friend_url}
                                external
                                variant="primary"
                              >
                                {t("LINEで友だち追加")}
                              </Button>
                            </InlineStack>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {t("PC・別の端末の方: QRコードを LINE で読み取り")}
                            </Text>
                            <QRCodeSVG value={line.add_friend_url} size={144} />
                          </BlockStack>
                        )}
                      </BlockStack>
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {t("ステップ2: トークで連携コードを送信")}
                        </Text>
                        {line.link_code && (
                          <InlineStack gap="300" blockAlign="center">
                            <Text as="span" variant="headingLg">
                              {line.link_code}
                            </Text>
                            <Button onClick={() => handleCopyCode(line.link_code!)}>
                              {codeCopied ? t("コピーしました") : t("コードをコピー")}
                            </Button>
                          </InlineStack>
                        )}
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t("コードをコピーして LINE のトークに貼り付け・送信すると、自動で連携が完了します（このページを再読み込みするとステータスが更新されます）。")}
                        </Text>
                      </BlockStack>
                    </BlockStack>

                    <Divider />

                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t("コードが外部に漏れたときは再発行してください。いま受信中のメンバーはそのまま残り、古いコードは使えなくなります。")}
                      </Text>
                      {rotateCodeMutation.isError && (
                        <Banner tone="critical">
                          {t("コードを再発行できませんでした:")}{" "}
                          {rotateCodeMutation.error.message}
                        </Banner>
                      )}
                      {disconnectMutation.isError && (
                        <Banner tone="critical">
                          {t("解除に失敗しました:")}{" "}
                          {disconnectMutation.error.message}
                        </Banner>
                      )}
                      <InlineStack align="end" gap="200">
                        <Button
                          onClick={() => rotateCodeMutation.mutate()}
                          loading={rotateCodeMutation.isPending}
                        >
                          {t("連携コードを再発行")}
                        </Button>
                        {recipients.length > 0 && (
                          <Button
                            tone="critical"
                            onClick={() => disconnectMutation.mutate()}
                            loading={disconnectMutation.isPending}
                          >
                            {t("全員の連携を解除")}
                          </Button>
                        )}
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
